import { useState } from 'react';
import { V2Card, V2CardHeader, V2Button, V2FormActions, Icon } from '@sakhtar/design-system/react';
import type { ModuleQuestionConfig, OtherOption, QuestionField } from './moduleAnswerConfig.js';
import type { FieldProblem } from '../../lib/errorEnvelope.js';
import { groupFieldProblems, normalizeForSave } from './moduleAnswersPatch.js';

type Draft = Record<string, unknown>;

/** One line under the offending field, naming it as the exact place to fix
 * — the generic top-of-step banner alone left the user unable to tell which
 * of several fields on a page was rejected (e.g. an empty warehouse-name
 * row buried in a multi-item list). Doesn't repeat the raw backend message
 * (an internal contract description, e.g. "non-empty string, max 120"),
 * since that's not written for an end user. */
function FieldError({ problems }: { problems: readonly FieldProblem[] | undefined }) {
  if (!problems || problems.length === 0) return null;
  const hasIndexed = problems.some((p) => /\[\d+\]$/.test(p.path));
  return (
    <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>
      {hasIndexed
        ? 'یکی از موارد این فهرست خالی یا نامعتبر است — همهٔ ردیف‌ها را بررسی کنید.'
        : 'مقدار واردشده برای این فیلد نامعتبر است.'}
    </span>
  );
}

/** Text-filtered dropdown over a fixed option list, with toggleable
 * selection (single or multi, per caller) — for a field whose option list
 * is too long to scan as a chip row (e.g. counting units). Selected values
 * render as removable chips above the search input. */
export function SearchableSelect({
  options,
  selected,
  placeholder,
  onToggle,
}: {
  options: readonly { readonly value: string; readonly label: string }[];
  selected: readonly string[];
  placeholder: string;
  onToggle: (value: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <div style={{ position: 'relative' }}>
      {selected.length > 0 ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {selected.map((v) => (
            <span key={v} style={chipStyle(true)} onClick={() => onToggle(v)}>
              {labelOf(v)} ✕
            </span>
          ))}
        </div>
      ) : null}
      <input
        className="sk-input"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && filtered.length > 0 ? (
        <div
          style={{
            position: 'absolute',
            zIndex: 5,
            top: 'calc(100% + 4px)',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            maxHeight: 220,
            overflowY: 'auto',
            background: 'var(--sk-bg-elev)',
            border: '1px solid var(--sk-border)',
            borderRadius: 'var(--sk-r-md)',
            boxShadow: 'var(--sk-shadow-3)',
          }}
        >
          {filtered.map((opt) => {
            const active = selected.includes(opt.value);
            return (
              <div
                key={opt.value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onToggle(opt.value);
                }}
                style={{
                  padding: '9px 12px',
                  fontSize: 13,
                  cursor: 'pointer',
                  color: active ? 'var(--sk-brand-600)' : 'var(--sk-fg)',
                  fontWeight: active ? 600 : 400,
                  background: active
                    ? 'color-mix(in oklch, var(--sk-brand-500), transparent 92%)'
                    : undefined,
                }}
              >
                {opt.label}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function chipStyle(active: boolean): React.CSSProperties {
  return {
    padding: '8px 14px',
    borderRadius: 'var(--sk-r-full, 999px)',
    border: `1px solid ${active ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
    background: active
      ? 'color-mix(in oklch, var(--sk-brand-500), transparent 88%)'
      : 'transparent',
    color: active ? 'var(--sk-brand-500)' : 'var(--sk-fg)',
    fontSize: 13,
    fontWeight: active ? 600 : 500,
    cursor: 'pointer',
  };
}

/** Renders a field's own `other.noteKey` companion text input once its
 * 'other' option is selected — shared by every chip/searchable-select
 * field kind that declares one. */
function OtherNote({
  other,
  active,
  draft,
  setField,
  fieldProblems,
}: {
  other: OtherOption | undefined;
  active: boolean;
  draft: Draft;
  setField: (key: string, value: unknown) => void;
  fieldProblems: ReadonlyMap<string, FieldProblem[]>;
}) {
  if (!other || !active) return null;
  const value = typeof draft[other.noteKey] === 'string' ? (draft[other.noteKey] as string) : '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <input
        className="sk-input"
        placeholder="توضیح دهید…"
        value={value}
        onChange={(e) => setField(other.noteKey, e.target.value || undefined)}
      />
      <FieldError problems={fieldProblems.get(other.noteKey)} />
    </div>
  );
}

function FieldRow({
  field,
  draft,
  setField,
  fieldProblems,
}: {
  field: QuestionField;
  draft: Draft;
  setField: (key: string, value: unknown) => void;
  fieldProblems: ReadonlyMap<string, FieldProblem[]>;
}) {
  const value = draft[field.key];
  const onChange = (next: unknown) => setField(field.key, next);
  const ownProblems = fieldProblems.get(field.key);

  if (field.kind === 'chip-single') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{field.label}</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {field.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              style={chipStyle(value === opt.value)}
              onClick={() => onChange(value === opt.value ? undefined : opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <FieldError problems={ownProblems} />
        <OtherNote
          other={field.other}
          active={field.other !== undefined && value === field.other.value}
          draft={draft}
          setField={setField}
          fieldProblems={fieldProblems}
        />
      </div>
    );
  }
  if (field.kind === 'chip-multi') {
    const selected = Array.isArray(value) ? (value as string[]) : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{field.label}</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {field.options.map((opt) => {
            const active = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                style={chipStyle(active)}
                onClick={() =>
                  onChange(
                    active ? selected.filter((v) => v !== opt.value) : [...selected, opt.value],
                  )
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <FieldError problems={ownProblems} />
        <OtherNote
          other={field.other}
          active={field.other !== undefined && selected.includes(field.other.value)}
          draft={draft}
          setField={setField}
          fieldProblems={fieldProblems}
        />
      </div>
    );
  }
  if (field.kind === 'radio') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{field.label}</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {field.options.map((opt) => {
            const active = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(active ? undefined : opt.value)}
                style={{
                  textAlign: 'start',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '11px 13px',
                  borderRadius: 'var(--sk-r-lg)',
                  border: `1.5px solid ${active ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                  background: active
                    ? 'color-mix(in oklch, var(--sk-brand-500), transparent 94%)'
                    : 'var(--sk-bg-canvas)',
                }}
              >
                <span
                  style={{
                    marginTop: 2,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: `1.5px solid ${active ? 'var(--sk-brand-500)' : 'var(--sk-border-strong)'}`,
                    display: 'grid',
                    placeContent: 'center',
                    flex: '0 0 auto',
                  }}
                >
                  {active ? (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'var(--sk-brand-500)',
                      }}
                    />
                  ) : null}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
                    {opt.label}
                  </span>
                  {opt.description ? (
                    <span style={{ fontSize: 11.5, color: 'var(--sk-fg-muted)' }}>
                      {opt.description}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
        <FieldError problems={ownProblems} />
      </div>
    );
  }
  if (field.kind === 'searchable-select' || field.kind === 'searchable-multi-select') {
    const isMulti = field.kind === 'searchable-multi-select';
    const selected = isMulti
      ? Array.isArray(value)
        ? (value as string[])
        : []
      : typeof value === 'string'
        ? [value]
        : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{field.label}</span>
        <SearchableSelect
          options={field.options}
          selected={selected}
          placeholder={field.placeholder}
          onToggle={(optValue) => {
            if (isMulti) {
              onChange(
                selected.includes(optValue)
                  ? selected.filter((v) => v !== optValue)
                  : [...selected, optValue],
              );
            } else {
              onChange(selected.includes(optValue) ? undefined : optValue);
            }
          }}
        />
        <FieldError problems={ownProblems} />
        <OtherNote
          other={field.other}
          active={field.other !== undefined && selected.includes(field.other.value)}
          draft={draft}
          setField={setField}
          fieldProblems={fieldProblems}
        />
      </div>
    );
  }
  if (field.kind === 'boolean') {
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
      >
        <span style={{ fontSize: 13, fontWeight: 600 }}>{field.label}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            style={chipStyle(value === true)}
            onClick={() => onChange(value === true ? undefined : true)}
          >
            بله
          </button>
          <button
            type="button"
            style={chipStyle(value === false)}
            onClick={() => onChange(value === false ? undefined : false)}
          >
            خیر
          </button>
        </div>
        <FieldError problems={ownProblems} />
      </div>
    );
  }
  // string-list
  const items = Array.isArray(value) ? (value as string[]) : [];
  if (field.maxItems === 1) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{field.label}</span>
        <input
          className="sk-input"
          value={items[0] ?? ''}
          placeholder={field.placeholder}
          onChange={(e) => {
            const trimmed = e.target.value;
            onChange(trimmed === '' ? undefined : [trimmed]);
          }}
        />
        <FieldError problems={ownProblems} />
      </div>
    );
  }
  const atMax = field.maxItems !== undefined && items.length >= field.maxItems;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{field.label}</span>
      {items.map((item, index) => (
        <div key={index} style={{ display: 'flex', gap: 8 }}>
          <input
            className="sk-input"
            style={{ flex: 1 }}
            value={item}
            placeholder={field.placeholder}
            onChange={(e) => {
              const next = [...items];
              next[index] = e.target.value;
              onChange(next);
            }}
          />
          <V2Button
            variant="ghost"
            size="sm"
            onClick={() => onChange(items.filter((_, i) => i !== index))}
          >
            حذف
          </V2Button>
        </div>
      ))}
      {!atMax ? (
        <V2Button variant="secondary" size="sm" onClick={() => onChange([...items, ''])}>
          + افزودن
        </V2Button>
      ) : null}
      <FieldError problems={ownProblems} />
    </div>
  );
}

/**
 * Generic Phase B module Q&A screen — one instance per `ModuleStepKey`,
 * driven entirely by `MODULE_QUESTION_CONFIG`. Card/chip answers per
 * docs/agents/handoffs/onboarding-design-review.md, Part 2; a visible
 * **skip** action distinct from "done" (not an error state); a step can be
 * completed having answered nothing at all — there is no required field
 * here, unlike `base-organization`.
 *
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Onboarding Setup Desktop.html — Phase B per-module Q&A
 *   wizard (5 modules), re-verified 2026-09-20 by rendering the file
 *   directly (not just re-reading the prior handoff): every real field in
 *   `moduleAnswerConfig.ts` (sales/CRM model+lead-sources+start-point+
 *   quote+follow-up; inventory warehouse-model+names+counting-units+
 *   serial/expiry tracking; procurement request-creators+PR-requirement+
 *   fixed-suppliers+need-by-date; finance fiscal-year-month+existing-CoA+
 *   posting-approval+VAT; projects task-model+single-owner+due-date+
 *   checklists+comments) matches the board's own paraphrased questions
 *   verbatim, confirmed field-by-field, not just at the module-title level.
 * archetype: card/chip-based Q&A, editable, skip ≠ error, final summary on
 *   `ReviewStep.tsx`.
 * status: PARTIAL_REAL — every field here maps 1:1 to a real column the
 *   backend accepts (`apps/api/src/setup/module-answers.ts`'s per-step
 *   schema); the board's own left-rail per-module progress indicator and
 *   its per-module "editable summary card before commit" (shown inline,
 *   per-module) are not built as separate sub-screens — `ReviewStep.tsx`
 *   is the one real summary, covering all steps at once instead of one per
 *   module, a real but coarser equivalent, not a fabricated substitute.
 * state reference: ready (all fields optional, no fetch on this screen —
 *   `config`/`initialAnswers` come from the parent wizard's already-loaded
 *   `SetupView`) · busy/error passed down from the parent's save/complete
 *   call
 */
export function ModuleAnswersStep({
  config,
  initialAnswers,
  onSave,
  onComplete,
  onBack,
  busy,
  error,
  errorDetails,
}: {
  config: ModuleQuestionConfig;
  initialAnswers: Record<string, unknown>;
  onSave: (patch: Draft) => Promise<void>;
  onComplete: (patch: Draft) => Promise<void>;
  onBack: () => void;
  busy: boolean;
  error: string | null;
  errorDetails?: readonly FieldProblem[] | undefined;
}) {
  const [draft, setDraft] = useState<Draft>(initialAnswers);
  const fieldProblems = groupFieldProblems(errorDetails);

  function setField(key: string, value: unknown) {
    setDraft((prev) => {
      const next = { ...prev };
      if (value === undefined || (Array.isArray(value) && value.length === 0)) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }

  // Only send fields that actually changed value-wise vs. what's already
  // saved, plus an explicit `null` for anything cleared — mirrors the
  // "each key optional, null clears, omitted keeps previous" contract.
  //
  // Trims strings and drops empty items before sending: the backend rejects
  // a whitespace-only string-list item outright (module-answers.ts's
  // `stringArray` validator), and the "add row" button leaves an empty
  // string in the draft until the user types into it, so an unsubmitted
  // trailing row (or one left blank by mistake) silently failed the whole
  // step's save with a single 400 that named the row's index but not which
  // on-screen field it belonged to — see FieldError above for that half.
  function patchOf(): Draft {
    const patch: Draft = {};
    const keys = new Set([...Object.keys(initialAnswers), ...Object.keys(draft)]);
    for (const key of keys) {
      const value = normalizeForSave(draft[key]);
      if (value === undefined) {
        if (initialAnswers[key] !== undefined) patch[key] = null;
      } else {
        patch[key] = value;
      }
    }
    return patch;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader title={config.title} subtitle={config.subtitle} />
        <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 22 }}>
          {config.fields
            .filter((field) => !field.showIf || draft[field.showIf.key] === field.showIf.equals)
            .map((field) => (
              <FieldRow
                key={field.key}
                field={field}
                draft={draft}
                setField={setField}
                fieldProblems={fieldProblems}
              />
            ))}
        </div>
      </V2Card>

      {error ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '13px 16px',
            borderRadius: 'var(--sk-r-md)',
            background: 'var(--sk-danger-soft)',
            border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
          }}
        >
          <span style={{ color: 'var(--sk-danger)', display: 'flex' }}>
            <Icon name="x" size="sm" />
          </span>
          <span style={{ fontSize: 13, color: 'var(--sk-fg)' }}>{error}</span>
        </div>
      ) : null}

      <V2FormActions>
        <V2Button variant="ghost" disabled={busy} onClick={onBack}>
          بازگشت
        </V2Button>
        <span style={{ marginInlineEnd: 'auto' }} />
        <V2Button variant="secondary" disabled={busy} onClick={() => void onSave(patchOf())}>
          {busy ? 'در حال ذخیره…' : 'ذخیرهٔ پیش‌نویس'}
        </V2Button>
        <V2Button
          variant="primary"
          icon="check"
          disabled={busy}
          onClick={() => void onComplete(patchOf())}
        >
          {busy ? 'در حال ثبت…' : 'تکمیل و ادامه'}
        </V2Button>
      </V2FormActions>
    </div>
  );
}
